import { renderApiProfileUiPath, renderViewUiPath } from '@upright/common/renderer/renderer-settings';

/**
 * Access token for the headless render view (PLT-5056).
 *
 * Server-side renders (`POST /api/render`, `/share-img/...`) load the SPA at
 * `/render/chart` or `/render-api-profile` inside upshot's headless Chromium
 * and pass the caller's JWT as an `?accessToken=` query parameter
 * (`upserve/modules/renderer/generate-render.ts`). The headless browser has
 * no `up-access` cookie, so without this token the render page's API calls
 * go out anonymous, 401, and upshot screenshots the React error boundary
 * instead of the chart.
 *
 * PLT-4720 removed the generic lift-token-from-URL code together with the
 * localStorage token store, which silently broke that flow. This module
 * restores it narrowly: the token is only read on the dedicated render
 * paths and is held in module memory — never persisted — preserving the
 * PLT-4720 XSS-hardening intent.
 */
const renderViewPaths = [renderViewUiPath, renderApiProfileUiPath];

let cachedToken: string | null | undefined;

export function getRenderViewAccessToken(): string | null {
  // The token is cached from the first read so it survives the render view
  // rewriting its own query string (control UI), but it is only ever
  // *returned* while still on a render path — if a human opens a render URL
  // and SPA-navigates away, auth falls back to the normal cookie flow.
  if (!isOnRenderViewPath()) {
    return null;
  }
  if (cachedToken === undefined) {
    cachedToken = readTokenFromSearch();
  }
  return cachedToken;
}

function isOnRenderViewPath(): boolean {
  try {
    if (typeof window === 'undefined') {
      return false;
    }
    const { pathname } = window.location;
    return renderViewPaths.some(path => pathname === path || pathname.startsWith(`${path}/`));
  } catch {
    return false;
  }
}

function readTokenFromSearch(): string | null {
  try {
    return new URLSearchParams(window.location.search).get('accessToken');
  } catch {
    return null;
  }
}
